import React, { useCallback, useEffect, useState } from 'react';

import { RowButton, SmallButton } from '@eika/button';
import { Icon, IconTypes } from '@eika/icon';
import { Input } from '@eika/input';
import { ModalDialogType } from '@eika/modal';

import type { Kid } from '../../../generated-types';
import { useNoteMaxLengthWithKIDSelected } from '../../../hooks/useNoteMaxLength';
import useTranslation, { format } from '../../../hooks/useTranslation';
import { formatKid } from '../../../services/kidNumberService';
import { EikaModal } from '../../EikaModal';
import NoteFieldLayout from '../../NoteField/NoteFieldLayout';
import { StickyFooterRow } from '../../shared/StickyFooter';
import { SelectedReceiver } from '../SelectedReceiver';
import { validateKid } from './form/validate';

interface Props {
    kid?: Kid;
    labelText?: string;
    placeholder: string;
    error?: string;
    locked?: boolean;
    disabled?: boolean;
    onChange: (value?: Kid) => void;
    onShow?: (show: boolean) => void;
    animateModalAs?: ModalDialogType;
    toAccount?: SelectedReceiver;
}

const KidField: React.FC<Props> = ({
    kid,
    labelText,
    placeholder,
    error,
    onChange,
    onShow,
    locked = false,
    disabled,
    animateModalAs,
    toAccount,
}) => {
    const translated = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [KIDstring, setKIDstring] = useState('');

    const [localError, setLocalError] = useState<string>();

    const triggerModal = (show: boolean) => {
        setLocalError(undefined);
        setShowModal(show);
        onShow?.(show);
    };

    const maxLength = useNoteMaxLengthWithKIDSelected({ selectedReceiver: toAccount });

    const onChangeEvent = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setLocalError(undefined);
            const targetValue = e.target.value.replace(/[^0-9-]/g, '');
            const snipped = targetValue.length > maxLength ? targetValue.substring(0, maxLength) : targetValue;
            if (targetValue.length > maxLength) {
                setLocalError(format(translated.tooManyCharactersKID, maxLength));
            }

            setKIDstring(snipped);
        },
        [setKIDstring, maxLength, translated.tooManyCharactersKID],
    );

    useEffect(() => {
        if (kid) {
            setKIDstring(kid.value);
        }
    }, [kid]);

    const onClose = () => {
        const errors = validateKid(translated, KIDstring, toAccount);
        if (errors) {
            setLocalError(errors);
        } else {
            triggerModal(false);
            onChange({ value: KIDstring });
        }
    };

    return (
        <>
            <RowButton onClick={() => triggerModal(true)} disabled={disabled || locked} className="kid-field__row-btn">
                <NoteFieldLayout
                    placeholder={placeholder}
                    label={labelText}
                    note={kid ? formatKid(kid) : ''}
                    icon={locked ? <Icon type={IconTypes.PIN_KODE_24} ariaLabel="Låst" /> : IconTypes.LITEN_PIL_HOEYRE}
                    error={error}
                />
            </RowButton>
            <EikaModal
                id="avtalegiroNoteField"
                open={showModal}
                setOpen={triggerModal}
                onClose={() => {
                    setKIDstring(kid?.value ?? '');
                }}
                title={labelText}
                onSubmit={onClose}
                animateAs={animateModalAs}
            >
                <Input
                    inputMode="numeric"
                    type="text"
                    placeholder={placeholder}
                    value={KIDstring}
                    onChange={onChangeEvent}
                    error={localError}
                    maxLength={maxLength + 1}
                    data-focus-first
                />
                <StickyFooterRow>
                    <SmallButton onClick={onClose}>{translated.continue}</SmallButton>
                </StickyFooterRow>
            </EikaModal>
        </>
    );
};

export default KidField;
